import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import {
  PlusCircle, Search, Download, ArrowUpDown, Loader2, TrendingUp, TrendingDown, Sparkles,
} from 'lucide-react';
import { usePortfolioStore } from '../../../state/portfolioStore';
import PageHeader from '../../../components/layout/PageHeader';
import SectionNav from '../../../components/layout/SectionNav';
import { portfolioTabs } from '../../../nav/publicNav';

function SortHeader({ label, sortKey, currentSort, onSort }) {
  const active = currentSort.key === sortKey;
  return (
    <button
      className="flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground hover:text-foreground transition-colors"
      onClick={() => onSort(sortKey)}
    >
      {label}
      {active && <ArrowUpDown className="w-3 h-3" />}
    </button>
  );
}

export default function HoldingsPage() {
  const navigate = useNavigate();
  const overview = usePortfolioStore((s) => s.overview);
  const overviewLoading = usePortfolioStore((s) => s.overviewLoading);
  const fetchOverview = usePortfolioStore((s) => s.fetchOverview);
  const holdings = usePortfolioStore((s) => s.holdings);
  const fetchHoldings = usePortfolioStore((s) => s.fetchHoldings);
  const seedSample = usePortfolioStore((s) => s.seedSample);

  const [search, setSearch] = useState('');
  const [sectorFilter, setSectorFilter] = useState('all');
  const [pnlFilter, setPnlFilter] = useState('all');
  const [sort, setSort] = useState({ key: 'currentValue', dir: 'desc' });
  const [seeding, setSeeding] = useState(false);

  useEffect(() => {
    if (!holdings.length) fetchHoldings();
    if (!overview) fetchOverview();
  }, [fetchHoldings, fetchOverview, holdings.length, overview]);

  const handleSeed = async () => { setSeeding(true); await seedSample(); setSeeding(false); };

  const allHoldings = overview?.holdings || holdings;

  const sectors = useMemo(() => {
    const s = new Set(allHoldings.map((h) => h.sector).filter(Boolean));
    return ['all', ...Array.from(s).sort()];
  }, [allHoldings]);

  const filtered = useMemo(() => {
    let list = [...allHoldings];
    if (search) {
      const q = search.toLowerCase();
      list = list.filter((h) => h.symbol.toLowerCase().includes(q) || (h.company_name || '').toLowerCase().includes(q));
    }
    if (sectorFilter !== 'all') {
      list = list.filter((h) => h.sector === sectorFilter);
    }
    if (pnlFilter === 'profit') list = list.filter((h) => (h.unrealizedPnL || 0) >= 0);
    if (pnlFilter === 'loss') list = list.filter((h) => (h.unrealizedPnL || 0) < 0);

    list.sort((a, b) => {
      const av = a[sort.key] ?? 0;
      const bv = b[sort.key] ?? 0;
      return sort.dir === 'asc' ? av - bv : bv - av;
    });
    return list;
  }, [allHoldings, search, sectorFilter, pnlFilter, sort]);

  const handleSort = (key) => {
    setSort((prev) => ({
      key,
      dir: prev.key === key && prev.dir === 'desc' ? 'asc' : 'desc',
    }));
  };

  const handleExportCSV = () => {
    const headers = ['Symbol', 'Company', 'Sector', 'Exchange', 'Qty', 'Avg Cost', 'Current Price', 'Current Value', 'Invested', 'P&L', 'P&L %', 'Weight %'];
    const rows = filtered.map((h) => [
      h.symbol, h.company_name, h.sector, h.exchange, h.quantity,
      (h.avg_buy_price || h.avgBuyPrice || 0).toFixed(2),
      (h.currentPrice || 0).toFixed(2),
      (h.currentValue || 0).toFixed(2),
      (h.investedValue || 0).toFixed(2),
      (h.unrealizedPnL || 0).toFixed(2),
      (h.unrealizedPnLPercent || 0).toFixed(2),
      (h.weight || 0).toFixed(2),
    ]);
    const csv = [headers, ...rows].map((r) => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'portfolio_holdings.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const formatCurrency = (v) => {
    if (v == null) return '₹0';
    return `₹${Number(v).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
  };

  return (
    <div className="space-y-4">
      <SectionNav tabs={portfolioTabs} />
      <PageHeader
        title="Holdings"
        description={`${filtered.length} of ${allHoldings.length} holdings`}
        actions={
          <div className="flex gap-2">
            <Button size="sm" variant="outline" className="text-xs gap-1.5" onClick={handleExportCSV}>
              <Download className="w-3 h-3" /> Export CSV
            </Button>
            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1.5 text-xs" onClick={() => navigate('/public/portfolio/add-holding')}>
              <PlusCircle className="w-3.5 h-3.5" /> Add
            </Button>
          </div>
        }
      />

      {/* Onboarding banner when the portfolio is empty or very small */}
      {allHoldings.length === 0 && (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-4 py-3 rounded-xl border border-emerald-500/20 bg-emerald-500/5">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-emerald-500 shrink-0" />
            <p className="text-xs text-muted-foreground">
              <span className="font-medium text-foreground">No holdings yet.</span> Connect your broker for live sync, or add manually.
            </p>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" className="text-xs h-7"
              onClick={() => navigate('/public/portfolio/broker')}>Connect broker</Button>
            <Button size="sm" className="text-xs h-7 bg-emerald-600 hover:bg-emerald-700 text-white"
              onClick={() => navigate('/public/portfolio/add-holding')}>Add a holding</Button>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-2">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input className="pl-9 h-8 text-xs" placeholder="Search by symbol or company..." value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <Select value={sectorFilter} onValueChange={setSectorFilter}>
          <SelectTrigger className="w-[160px] h-8 text-xs"><SelectValue placeholder="Sector" /></SelectTrigger>
          <SelectContent>
            {sectors.map((s) => (
              <SelectItem key={s} value={s}>{s === 'all' ? 'All Sectors' : s}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={pnlFilter} onValueChange={setPnlFilter}>
          <SelectTrigger className="w-[120px] h-8 text-xs"><SelectValue placeholder="P&L" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="profit">Profit</SelectItem>
            <SelectItem value="loss">Loss</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {overviewLoading && !overview ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <Card className="border-border/50">
          <CardContent className="p-0">
            {/* Table Header */}
            <div className="hidden md:grid grid-cols-[2fr_1fr_1fr_1fr_1fr_1fr_1fr_0.8fr] gap-2 px-4 py-2 border-b border-border/50 bg-secondary/30">
              <SortHeader label="Company" sortKey="company_name" currentSort={sort} onSort={handleSort} />
              <SortHeader label="Sector" sortKey="sector" currentSort={sort} onSort={handleSort} />
              <SortHeader label="Qty" sortKey="quantity" currentSort={sort} onSort={handleSort} />
              <SortHeader label="Avg Cost" sortKey="avg_buy_price" currentSort={sort} onSort={handleSort} />
              <SortHeader label="Current" sortKey="currentPrice" currentSort={sort} onSort={handleSort} />
              <SortHeader label="Value" sortKey="currentValue" currentSort={sort} onSort={handleSort} />
              <SortHeader label="P&L %" sortKey="unrealizedPnLPercent" currentSort={sort} onSort={handleSort} />
              <SortHeader label="Weight" sortKey="weight" currentSort={sort} onSort={handleSort} />
            </div>
            {/* Rows */}
            <div className="divide-y divide-border/30">
              {filtered.map((h) => {
                const pnl = h.unrealizedPnL || 0;
                const pnlPct = h.unrealizedPnLPercent || 0;
                const dayPct = h.dayChangePercent || 0;
                return (
                  <button
                    key={h.id || h.symbol}
                    className="w-full text-left px-4 py-2.5 hover:bg-secondary/50 transition-colors"
                    onClick={() => navigate(`/public/portfolio/stock/${h.symbol}`)}
                  >
                    {/* Desktop */}
                    <div className="hidden md:grid grid-cols-[2fr_1fr_1fr_1fr_1fr_1fr_1fr_0.8fr] gap-2 items-center">
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">{h.company_name || h.symbol}</p>
                        <div className="flex items-center gap-1.5">
                          <span className="text-[11px] font-mono text-muted-foreground">{h.symbol}</span>
                          <span className="text-[10px] text-muted-foreground">{h.exchange}</span>
                        </div>
                      </div>
                      <span className="text-xs text-muted-foreground">{h.sector || '—'}</span>
                      <span className="text-xs">{h.quantity}</span>
                      <span className="text-xs">{formatCurrency(h.avg_buy_price || h.avgBuyPrice)}</span>
                      <div>
                        <span className="text-xs font-medium">{formatCurrency(h.currentPrice)}</span>
                        <span className={`text-[10px] ml-1 ${dayPct >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                          {dayPct >= 0 ? '+' : ''}{dayPct.toFixed(1)}%
                        </span>
                      </div>
                      <span className="text-xs font-semibold">{formatCurrency(h.currentValue)}</span>
                      <div className="flex items-center gap-1">
                        {pnl >= 0 ? <TrendingUp className="w-3 h-3 text-emerald-500" /> : <TrendingDown className="w-3 h-3 text-red-500" />}
                        <span className={`text-xs font-semibold ${pnl >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                          {pnlPct >= 0 ? '+' : ''}{pnlPct.toFixed(1)}%
                        </span>
                      </div>
                      <span className="text-xs text-muted-foreground">{(h.weight || 0).toFixed(1)}%</span>
                    </div>
                    {/* Mobile */}
                    <div className="md:hidden flex items-center justify-between">
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">{h.company_name || h.symbol}</p>
                        <p className="text-[11px] text-muted-foreground">{h.symbol} · {h.quantity} shares</p>
                      </div>
                      <div className="text-right shrink-0 ml-3">
                        <p className="text-sm font-semibold">{formatCurrency(h.currentValue)}</p>
                        <p className={`text-xs ${pnl >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                          {pnlPct >= 0 ? '+' : ''}{pnlPct.toFixed(1)}%
                        </p>
                      </div>
                    </div>
                  </button>
                );
              })}
              {filtered.length === 0 && (
                <div className="py-12 text-center text-sm text-muted-foreground">No holdings match your filters</div>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
